import { useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate, fmtCAD, fmtKm, downloadCSV, todayISO } from "@/lib/format";
import { shareTripCalendar, emailTripInvite } from "@/lib/calendar";
import TripFormDialog from "@/components/forms/TripFormDialog";
import LogTripButton from "@/components/forms/LogTripButton";
import PrescriptionFormDialog from "@/components/forms/PrescriptionFormDialog";
import PremiumFormDialog from "@/components/forms/PremiumFormDialog";
import {
  getFamilyMembers, parseNotesMember, subscribeToFamilyChanges,
} from "@/lib/familyMembers";
import ReceiptViewer from "@/components/ReceiptViewer";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Car, Receipt, Pill, FileCsv, PencilSimple, Trash, Copy, FilePdf, EnvelopeSimple,
  ArrowsLeftRight, ArrowRight, Paperclip, MagnifyingGlass, Stethoscope, CaretDown, CaretUp,
  CalendarBlank, ShieldCheck, UsersThree, Plus
} from "@phosphor-icons/react";

function n(x) { return Number(x) || 0; }
function tripExpenses(t) {
  return n(t.parking) + n(t.tolls) + n(t.meals) + n(t.accommodation) +
         n(t.gas) + n(t.taxi) + n(t.rental) + n(t.other_amount);
}
function tripTotal(t) {
  return tripExpenses(t) + n(t.drug_amount);
}

// CRA simplified mileage rates ($/km) — keep in sync with backend
const CRA_MILEAGE_RATES = {
  AB: 0.55, BC: 0.57, MB: 0.565, NB: 0.585, NL: 0.605, NS: 0.575,
  NT: 0.725, NU: 0.71, ON: 0.62, PE: 0.57, QC: 0.59, SK: 0.54, YT: 0.705,
};

// CRA medical-travel eligibility by one-way distance.
// Source: CRA Income Tax Folio S1-F1-C1, Medical Expense Tax Credit.
// Tiers (one-way to nearest equivalent medical service):
//   < 40 km   → no travel expenses claimable (only Rx/services)
//   40–79 km  → vehicle (mileage), parking, tolls, public transit/taxi
//   ≥ 80 km   → all the above + meals + accommodation
function cmTier(t) {
  const ow = n(t.one_way_km);
  if (ow >= 80) return "80+";
  if (ow >= 40) return "40-79";
  return "<40";
}
// Returns the eligible portion of each travel expense category for one trip.
function eligibleTripBreakdown(t, mileageRate) {
  const tier = cmTier(t);
  const mileageDollars = n(t.total_km) * (Number(mileageRate) || 0);
  const cats = {
    mileage: 0, parking: 0, tolls: 0, meals: 0,
    accommodation: 0, gas: 0, taxi: 0, rental: 0, other: 0,
  };
  const ineligibleItems = []; // [{label, amount, reason}]
  if (tier === "80+") {
    cats.mileage = mileageDollars;
    cats.parking = n(t.parking);
    cats.tolls = n(t.tolls);
    cats.meals = n(t.meals);
    cats.accommodation = n(t.accommodation);
    cats.gas = n(t.gas);
    cats.taxi = n(t.taxi);
    cats.rental = n(t.rental);
    cats.other = n(t.other_amount);
  } else if (tier === "40-79") {
    cats.mileage = mileageDollars;
    cats.parking = n(t.parking);
    cats.tolls = n(t.tolls);
    cats.taxi = n(t.taxi);
    // meals, accommodation, gas, rental, other → not generally claimable in this tier
    [
      ["Meals", n(t.meals)],
      ["Accommodation", n(t.accommodation)],
      ["Gas (use mileage method)", n(t.gas)],
      ["Car rental", n(t.rental)],
      ["Other", n(t.other_amount)],
    ].forEach(([label, amount]) => {
      if (amount > 0) ineligibleItems.push({ label, amount, reason: "40–79 km trip" });
    });
  } else {
    // <40 km: nothing travel-eligible
    if (mileageDollars > 0) ineligibleItems.push({ label: "Mileage", amount: mileageDollars, reason: "Under 40 km" });
    [
      ["Parking", n(t.parking)],
      ["Tolls", n(t.tolls)],
      ["Meals", n(t.meals)],
      ["Accommodation", n(t.accommodation)],
      ["Gas", n(t.gas)],
      ["Taxi", n(t.taxi)],
      ["Car rental", n(t.rental)],
      ["Other", n(t.other_amount)],
    ].forEach(([label, amount]) => {
      if (amount > 0) ineligibleItems.push({ label, amount, reason: "Under 40 km" });
    });
  }
  const eligible = Object.values(cats).reduce((a, b) => a + b, 0);
  const claimed = tripExpenses(t) + mileageDollars;
  const ineligible = Math.max(0, claimed - eligible);
  return { tier, mileageDollars, cats, eligible, ineligible, ineligibleItems };
}

// --- Receipt appendix helpers ------------------------------------------------
// Convert a Blob to a data URL string ("data:image/jpeg;base64,...").
function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}
// Load an Image element from a data URL so jsPDF can render it sized.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
// jsPDF cannot embed PDFs or HEIC; only raster images we can decode.
function isEmbeddableImage(ct) {
  if (!ct) return false;
  return ct === "image/jpeg" || ct === "image/jpg" || ct === "image/png";
}
// Fetch a single receipt → { img, w, h } HTMLImageElement, or null when not embeddable.
async function fetchReceiptThumb(api, fileId) {
  try {
    const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const blob = res.data;
    const ct = blob.type || res.headers["content-type"] || "";
    if (!isEmbeddableImage(ct)) {
      return { fileId, contentType: ct, skipped: true };
    }
    const dataUrl = await blobToDataURL(blob);
    const img = await loadImage(dataUrl);
    const fmt = ct === "image/png" ? "PNG" : "JPEG";
    return { fileId, contentType: ct, img, fmt, w: img.naturalWidth, h: img.naturalHeight };
  } catch {
    return { fileId, skipped: true, error: true };
  }
}
// -----------------------------------------------------------------------------
function provinceLabel(code) {
  return ({
    AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
    NL: "Newfoundland & Labrador", NS: "Nova Scotia", NT: "Northwest Territories",
    NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island", QC: "Quebec",
    SK: "Saskatchewan", YT: "Yukon",
  }[code] || code || "—");
}

// Cached MedEasy logo (loaded once, downsized for PDF embedding)
let _logoCache = null;
async function loadMedEasyLogo() {
  if (_logoCache) return _logoCache;
  try {
    const res = await fetch("/medeasy-logo.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    // Load into an Image, downscale via canvas to ~200px square, export as JPEG (~10-20KB)
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    _logoCache = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const target = 240; // px (renders sharp at our 36pt PDF size on retina print)
          const canvas = document.createElement("canvas");
          canvas.width = target;
          canvas.height = target;
          const ctx = canvas.getContext("2d");
          // White background to flatten any transparency cleanly for JPEG
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, target, target);
          // Contain-fit the source image into the square
          const scale = Math.min(target / img.width, target / img.height);
          const w = img.width * scale, h = img.height * scale;
          const x = (target - w) / 2, y = (target - h) / 2;
          ctx.drawImage(img, x, y, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        } catch {
          resolve(dataUrl); // fallback to original
        }
      };
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    return _logoCache;
  } catch {
    return null;
  }
}

function Stat({ Icon, label, value, sublabel, testId, accent }) {
  return (
    <Card className="rounded-2xl border-[#E3E1D9] card-lift">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
            <Icon weight="duotone" size={22} className="text-white" />
          </div>
        </div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#5C6660] font-semibold">{label}</div>
        <div className="font-[Manrope] text-2xl sm:text-3xl font-bold mt-2 text-[#1E2420]" data-testid={testId}>{value}</div>
        {sublabel && <div className="text-sm text-[#5C6660] mt-1">{sublabel}</div>}
      </CardContent>
    </Card>
  );
}

export default function Home() {
  const [year, setYear] = useState(null);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all"); // all | trip | prescription

  // Family Mode (MVP): which family member's trips to show. "all" means
  // every trip regardless of `[Member]` prefix. Other values match the
  // bracketed prefix in the trip's notes (Self = trips with NO prefix
  // OR explicit [Self] prefix).
  const [memberFilter, setMemberFilter] = useState("all");
  const [familyList, setFamilyList] = useState(getFamilyMembers());
  useEffect(() => subscribeToFamilyChanges(setFamilyList), []);
  const [claimMode, setClaimMode] = useState("calendar"); // calendar | custom
  const [claimEndDate, setClaimEndDate] = useState(""); // ISO YYYY-MM-DD for custom 12-mo period end

  const qc = useQueryClient();
  const tripsQ = useQuery({
    queryKey: ["trips"],
    queryFn: async () => (await api.get("/trips")).data,
  });
  const summaryQ = useQuery({
    queryKey: ["summary", year],
    queryFn: async () => (await api.get("/summary", { params: year ? { year } : {} })).data,
  });

  const trips = useMemo(() => tripsQ.data || [], [tripsQ.data]);

  // Family Mode (MVP): chip names for the filter row. Combines the user's
  // configured family list (from localStorage) with any orphaned `[Name]`
  // tags found in existing trips — so the chip row still reflects
  // historical tags even after the user removed a member from Settings.
  const memberChipNames = useMemo(() => {
    const set = new Set(familyList);
    for (const t of trips) {
      const { member } = parseNotesMember(t.notes || "");
      if (member && member.toLowerCase() !== "self") set.add(member);
    }
    if (!Array.from(set).some((m) => m.toLowerCase() === "self")) {
      set.add("Self");
    }
    const arr = Array.from(set);
    arr.sort((a, b) => {
      if (a.toLowerCase() === "self") return -1;
      if (b.toLowerCase() === "self") return 1;
      return a.localeCompare(b);
    });
    return arr;
  }, [familyList, trips]);
  const totals = summaryQ.data?.totals || { km: 0, mileage_credit: 0, expenses: 0, drugs: 0, grand_total: 0, trip_count: 0 };
  const years = summaryQ.data?.available_years || [new Date().getFullYear()];
  const currentYear = summaryQ.data?.year || years[0];

  // Insurance-premium total — computed client-side because `category` filtering
  // isn't part of the /api/summary aggregation. A premium entry is an Rx/Service
  // entry tagged with category="Premium"; the annual cost lives in either
  // drug_amount or service_total (whichever the user filled in the form).
  const premiumsTotal = useMemo(() => {
    return (trips || [])
      .filter((t) => (year ? t.date?.slice(0, 4) === String(year) : true))
      .filter((t) => (t.category || "").toLowerCase() === "premium")
      .reduce((sum, t) => sum + n(t.drug_amount) + n(t.service_total), 0);
  }, [trips, year]);

  // Auto-jump to most recent year with trips if the default (current) year is empty
  const autoJumped = useRef(false);
  useEffect(() => {
    if (autoJumped.current) return;
    if (year != null) return;
    if (!summaryQ.data) return;
    const cy = new Date().getFullYear();
    if (totals.trip_count === 0 && trips.length > 0) {
      const years2 = [...new Set(trips.map((t) => Number(t.date?.slice(0, 4))).filter(Boolean))];
      const newest = Math.max(...years2);
      if (newest && newest !== cy) {
        autoJumped.current = true;
        setYear(newest);
      }
    }
  }, [summaryQ.data, totals.trip_count, trips, year]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return trips
      .filter((t) => (year ? t.date?.slice(0, 4) === String(year) : true))
      .filter((t) => {
        const et = t.entry_type || "trip";
        if (filterType === "all") return true;
        return et === filterType;
      })
      .filter((t) => {
        // Family-member filter — match the bracketed prefix in notes.
        // "all"  → no filter
        // "Self" → trips with NO bracket OR bracket === "Self"
        // other  → bracket name === filter (case-insensitive)
        if (memberFilter === "all") return true;
        const { member } = parseNotesMember(t.notes || "");
        return member.toLowerCase() === memberFilter.toLowerCase();
      })
      .filter((t) => {
        if (!term) return true;
        const blob = `${t.destination || ""} ${t.provider || ""} ${t.purpose || ""} ${t.drug_name || ""} ${t.drug_pharmacy || ""} ${t.notes || ""}`.toLowerCase();
        return blob.includes(term);
      })
      .sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.created_at || "").localeCompare(a.created_at || ""));
  }, [trips, year, q, filterType, memberFilter]);

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/trips/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      toast.success("Trip removed");
    },
  });

  const { user } = useAuth();
  const mileageRate = user?.custom_rate || CRA_MILEAGE_RATES[user?.province] || CRA_MILEAGE_RATES.ON;
  const provName = provinceLabel(user?.province || "ON");
  const taxpayerName = user?.name || user?.email || "Taxpayer";

  // CRA lets you claim ANY 12-month period ending in the tax year (not just calendar).
  // Most Canadians don't know this — picking the right window often adds 10-20% eligible.
  function claimRange() {
    if (claimMode === "custom" && claimEndDate) {
      const end = claimEndDate;
      const d = new Date(end + "T00:00:00");
      const start = new Date(d);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
      const startISO = start.toISOString().slice(0, 10);
      return { start: startISO, end, label: `12-month period: ${fmtDate(startISO)} → ${fmtDate(end)}`, slug: `${startISO}_to_${end}` };
    }
    const yr = year || currentYear;
    return {
      start: `${yr}-01-01`,
      end: `${yr}-12-31`,
      label: `Calendar ${yr}`,
      slug: `${yr}`,
    };
  }
  const inClaimRange = (t) => {
    const { start, end } = claimRange();
    return t.date && t.date >= start && t.date <= end;
  };

  const exportCSV = () => {
    const range = claimRange();
    const inRange = (t) => t.date && t.date >= range.start && t.date <= range.end;
    const rows = [[
      "Date","Type","Destination","Provider","Purpose",
      "One-way km","Round trip","Total km","CRA tier",
      "Mileage $","Parking","Tolls","Meals","Accommodation","Gas","Taxi","Rental","Other","Other desc",
      "Drug / Service","DIN","Pharmacy/Provider","Service total","Insurance covered","Out-of-pocket",
      "Eligible travel $","Ineligible travel $","Trip total CAD","Notes"
    ]];
    trips.filter(inRange).forEach((t) => {
      const isRx = t.entry_type === "prescription";
      const br = isRx
        ? { tier: "—", mileageDollars: 0, eligible: 0, ineligible: 0 }
        : eligibleTripBreakdown(t, mileageRate);
      rows.push([
        t.date,
        isRx ? (t.service_kind === "service" ? "Service" : "Prescription") : "Trip",
        t.destination || "", t.provider || "", t.purpose || "",
        t.one_way_km ?? "", t.round_trip ? "Yes" : "No", t.total_km ?? "", br.tier,
        br.mileageDollars.toFixed(2),
        n(t.parking).toFixed(2), n(t.tolls).toFixed(2), n(t.meals).toFixed(2),
        n(t.accommodation).toFixed(2), n(t.gas).toFixed(2), n(t.taxi).toFixed(2),
        n(t.rental).toFixed(2), n(t.other_amount).toFixed(2), t.other_desc || "",
        t.drug_name || "", t.din || "", t.drug_pharmacy || "",
        n(t.service_total).toFixed(2), n(t.service_covered).toFixed(2), n(t.drug_amount).toFixed(2),
        br.eligible.toFixed(2), br.ineligible.toFixed(2),
        (tripTotal(t) + br.mileageDollars).toFixed(2), t.notes || ""
      ]);
    });

    // Aggregate totals
    let totalElig = 0, totalIne = 0, totalMileage = 0, totalKmAll = 0;
    trips.filter(inRange).filter((t) => t.entry_type !== "prescription").forEach((t) => {
      const br = eligibleTripBreakdown(t, mileageRate);
      totalElig += br.eligible;
      totalIne += br.ineligible;
      totalMileage += br.mileageDollars;
      totalKmAll += n(t.total_km);
    });
    const totalRx = trips.filter(inRange).reduce((s, t) => s + n(t.drug_amount), 0);
    const claimable = totalElig + totalRx;

    rows.push([]);
    rows.push([`TOTALS — CRA Medical Expense Tax Credit (Line 33099) · ${range.label}`]);
    rows.push([`Mileage rate used (${provName})`, `$${mileageRate.toFixed(3)}/km`]);
    rows.push(["Total kilometres", totalKmAll.toFixed(1)]);
    rows.push(["Mileage claim ($)", totalMileage.toFixed(2)]);
    rows.push(["Eligible travel expenses", totalElig.toFixed(2)]);
    rows.push(["Ineligible travel expenses (excluded from claim)", totalIne.toFixed(2)]);
    rows.push(["Prescriptions, drugs & medical services (always eligible)", totalRx.toFixed(2)]);
    rows.push(["TOTAL CLAIMABLE — CRA Line 33099", claimable.toFixed(2)]);
    downloadCSV(`medtravel_${range.slug}_CRA_detail.csv`, rows);
  };

  const exportPDF = async () => {
    const range = claimRange();
    const inRange = (t) => t.date && t.date >= range.start && t.date <= range.end;
    const yearEntries = trips.filter(inRange);
    if (yearEntries.length === 0) { toast.error("No entries in this period to export"); return; }
    const logoDataUrl = await loadMedEasyLogo();

    // Split entries
    const yearTrips = yearEntries.filter((t) => t.entry_type !== "prescription");
    const yearRx = yearEntries.filter((t) => t.entry_type === "prescription");

    // Compute per-trip eligibility and aggregate
    const enriched = yearTrips.map((t) => ({ t, br: eligibleTripBreakdown(t, mileageRate) }));
    const tierCounts = { "80+": 0, "40-79": 0, "<40": 0 };
    let totalMileageClaim = 0, totalEligibleTravel = 0, totalIneligibleTravel = 0;
    let totalEligibleKm = 0, totalKmInPeriod = 0;
    enriched.forEach(({ t, br }) => {
      tierCounts[br.tier] = (tierCounts[br.tier] || 0) + 1;
      totalMileageClaim += br.cats.mileage;       // eligible mileage only
      totalEligibleTravel += br.eligible;
      totalIneligibleTravel += br.ineligible;
      totalKmInPeriod += n(t.total_km);
      if (br.cats.mileage > 0) totalEligibleKm += n(t.total_km);
    });
    // Rx total: sum of drug_amount across BOTH trip entries and standalone rx entries
    const totalRx = yearEntries.reduce((sum, t) => sum + n(t.drug_amount), 0);
    const totalClaimable = totalEligibleTravel + totalRx;

    // Aggregate ineligible items across all trips for the "Eligibility Review" section
    const ineligibleAgg = []; // [{label, amount, date, destination, reason}]
    let tripsEligible = 0, tripsExcluded = 0, tripsWithCompanion = 0;
    enriched.forEach(({ t, br }) => {
      const hadEligible = br.eligible > 0;
      if (hadEligible) tripsEligible += 1;
      const hadIneligible = br.ineligibleItems.length > 0;
      if (hadIneligible && !hadEligible) tripsExcluded += 1;
      if (t.companion_required) tripsWithCompanion += 1;
      br.ineligibleItems.forEach((it) => {
        ineligibleAgg.push({
          ...it,
          date: t.date,
          destination: t.destination || t.purpose || "Trip",
        });
      });
    });

    // === LANDSCAPE PDF ===
    const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    let cursorY = 40;

    // === HEADER STRIP — taxpayer + period ===
    const HEADER_H = 96;
    doc.setFillColor(30, 36, 32);                       // ink black
    doc.rect(0, 0, W, HEADER_H, "F");
    // Logo (left of title) — larger white pill for brand visibility on accountant desks
    let titleX = 40;
    if (logoDataUrl) {
      try {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(28, 14, 72, 72, 10, 10, "F");
        doc.addImage(logoDataUrl, "JPEG", 32, 18, 64, 64, undefined, "FAST");
        titleX = 116;
      } catch {
        // fall back to text-only title
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text("CRA Medical Expense Report", titleX, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(212, 163, 115);                    // ochre
    doc.text(range.label.toUpperCase(), titleX, 62);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(180, 180, 175);
    doc.text("Powered by MedEasy · mymedeasy.ca", titleX, 80);
    // Top-right: taxpayer block
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text(taxpayerName, W - 40, 36, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(200, 200, 195);
    doc.text(
      `${provName}  ·  Mileage rate $${mileageRate.toFixed(3)}/km  ·  Generated ${new Date().toLocaleDateString("en-CA")}`,
      W - 40, 56, { align: "right" }
    );
    cursorY = HEADER_H + 18;

    // === GIANT CLAIM TOTAL BOX ===
    const claimBoxH = 150;
    doc.setFillColor(44, 76, 59);                       // pine green
    doc.roundedRect(40, cursorY, W - 80, claimBoxH, 12, 12, "F");
    // Section title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(212, 163, 115);
    doc.text("CRA MEDICAL EXPENSE SUMMARY", 60, cursorY + 26);
    // Line items (left side)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(235, 233, 224);
    doc.text("Eligible Medical Travel", 60, cursorY + 56);
    doc.text("Eligible Prescriptions & Services", 60, cursorY + 78);
    // Amounts (right-aligned on left side)
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(fmtCAD(totalEligibleTravel), 420, cursorY + 56, { align: "right" });
    doc.text(fmtCAD(totalRx), 420, cursorY + 78, { align: "right" });
    // Divider
    doc.setDrawColor(212, 163, 115);
    doc.setLineWidth(0.8);
    doc.line(60, cursorY + 92, 420, cursorY + 92);
    // Total (giant)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(212, 163, 115);
    doc.text("TOTAL ELIGIBLE MEDICAL EXPENSES", 60, cursorY + 112);
    doc.setFontSize(9);
    doc.setTextColor(200, 195, 180);
    doc.text("CRA Line 33099", 60, cursorY + 128);
    // GIANT NUMBER on the right
    doc.setFont("helvetica", "bold");
    doc.setFontSize(38);
    doc.setTextColor(255, 255, 255);
    doc.text(fmtCAD(totalClaimable), W - 60, cursorY + 124, { align: "right" });
    cursorY += claimBoxH + 16;

    // === CRA ELIGIBILITY REVIEW ===
    const reviewH = 148;
    doc.setFillColor(245, 243, 235);                    // bone
    doc.roundedRect(40, cursorY, W - 80, reviewH, 10, 10, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(44, 76, 59);
    doc.text("CRA ELIGIBILITY REVIEW", 60, cursorY + 22);

    // Left column: trip counts
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(40);
    const colLeftX = 60, valX = 360;
    doc.text("Trips entered:", colLeftX, cursorY + 42);
    doc.text("Trips with eligible expenses:", colLeftX, cursorY + 58);
    doc.text("Trips fully excluded (under 40 km):", colLeftX, cursorY + 74);
    doc.text("Trips with required companion:", colLeftX, cursorY + 90);
    doc.text("Prescriptions & services:", colLeftX, cursorY + 106);
    // Values
    doc.setFont("helvetica", "bold");
    doc.text(String(enriched.length), valX, cursorY + 42, { align: "right" });
    doc.setTextColor(63, 168, 94); // green
    doc.text(String(tripsEligible), valX, cursorY + 58, { align: "right" });
    doc.setTextColor(193, 119, 103); // terracotta
    doc.text(String(tripsExcluded), valX, cursorY + 74, { align: "right" });
    doc.setTextColor(44, 76, 59); // pine
    doc.text(String(tripsWithCompanion), valX, cursorY + 90, { align: "right" });
    doc.setTextColor(40);
    doc.text(
      String(yearEntries.filter((t) => n(t.drug_amount) > 0 || t.entry_type === "prescription").length),
      valX, cursorY + 106, { align: "right" }
    );

    // Right column: excluded expenses list
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(193, 119, 103);
    doc.text("Excluded expenses (not claimed):", 400, cursorY + 42);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60);
    if (ineligibleAgg.length === 0) {
      doc.setTextColor(63, 168, 94);
      doc.text("✓ None — every logged expense met CRA distance rules.", 400, cursorY + 60);
    } else {
      // Group ineligible items by label+reason so they sum
      const grouped = {};
      ineligibleAgg.forEach((it) => {
        const k = `${it.label} (${it.reason} trip)`;
        grouped[k] = (grouped[k] || 0) + it.amount;
      });
      const lines = Object.entries(grouped)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4);
      lines.forEach(([label, amount], i) => {
        doc.text(`• ${label}: ${fmtCAD(amount)}`, 400, cursorY + 60 + i * 14);
      });
      const totalLines = Object.keys(grouped).length;
      if (totalLines > 4) {
        doc.setTextColor(120);
        doc.text(`+ ${totalLines - 4} more — see detail table`, 400, cursorY + 60 + 4 * 14);
      }
    }
    cursorY += reviewH + 12;

    // === PREPARED BY MEDEASY callout ===
    const calloutLines = [
      "MedEasy automatically applied CRA medical travel distance rules and excluded ineligible expenses from the totals above.",
    ];
    if (tripsWithCompanion > 0) {
      calloutLines.push(
        `Companion travel claimed on ${tripsWithCompanion} trip${tripsWithCompanion === 1 ? "" : "s"} — practitioner certification on file with the taxpayer.`
      );
    }
    const calloutH = 28 + calloutLines.length * 12;
    doc.setFillColor(252, 242, 226);                    // soft cream
    doc.setDrawColor(212, 163, 115);
    doc.setLineWidth(0.6);
    doc.roundedRect(40, cursorY, W - 80, calloutH, 6, 6, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(140, 90, 43);
    doc.text("PREPARED BY MEDEASY", 56, cursorY + 14);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(60);
    calloutLines.forEach((line, i) => {
      doc.text(line, 56, cursorY + 28 + i * 12);
    });
    cursorY += calloutH + 18;

    // ========== AUDIT TRAIL — STARTS ON PAGE 2 ==========
    doc.addPage();
    cursorY = 40;
    // Section banner
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(30, 36, 32);
    doc.text("Itemized Audit Trail", 40, cursorY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(92, 102, 96);
    cursorY += 16;
    doc.text(
      "Detailed ledger of every logged trip and prescription/service. Use for CRA audit substantiation.",
      40, cursorY
    );
    cursorY += 18;

    // ========== 1. TRIP & TRAVEL LEDGER ==========
    if (enriched.length) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(44, 76, 59);
      doc.text(`1. Trip & Travel Ledger  ·  ${enriched.length} trip${enriched.length === 1 ? "" : "s"}`, 40, cursorY);
      cursorY += 6;

      // Build itemized travel-expense string per trip (each line broken so autoTable can wrap)
      const buildOtherExpenses = (t, br) => {
        const items = [];
        if (n(t.parking) > 0)       items.push(`Parking ${fmtCAD(n(t.parking))}`);
        if (n(t.tolls) > 0)         items.push(`Tolls ${fmtCAD(n(t.tolls))}`);
        if (n(t.accommodation) > 0) items.push(`Lodging ${fmtCAD(n(t.accommodation))}`);
        if (n(t.taxi) > 0)          items.push(`Taxi ${fmtCAD(n(t.taxi))}`);
        if (n(t.rental) > 0)        items.push(`Rental ${fmtCAD(n(t.rental))}`);
        if (n(t.gas) > 0)           items.push(`Gas ${fmtCAD(n(t.gas))}`);
        if (n(t.other_amount) > 0)  items.push(`${t.other_desc || "Other"} ${fmtCAD(n(t.other_amount))}`);
        return items.length ? items.join("\n") : "—";
      };
      const buildMealsClaim = (t, br) => {
        const meals = n(t.meals);
        if (meals === 0) return "—";
        if (br.tier === "80+") return `${fmtCAD(meals)} (simplified rate)`;
        return `${fmtCAD(meals)} · NOT ELIGIBLE (< 80 km)`;
      };
      const buildDestPurpose = (t) => {
        const dest = t.destination || "Trip";
        const purpose = t.purpose ? ` — ${t.purpose}` : "";
        const provider = t.provider ? `\n${t.provider}` : "";
        const companion = t.companion_required ? "\n+ Companion (certified)" : "";
        return `${dest}${purpose}${provider}${companion}`;
      };

      const tripRows = enriched.map(({ t, br }) => [
        fmtDate(t.date),
        buildDestPurpose(t),
        `${n(t.total_km).toFixed(0)} km\nTier ${br.tier}`,
        buildMealsClaim(t, br),
        buildOtherExpenses(t, br),
        fmtCAD(br.eligible),
      ]);

      autoTable(doc, {
        startY: cursorY,
        head: [[
          "Date",
          "Destination & Purpose",
          "Distance",
          "Meals Claimed",
          "Other Travel Expenses",
          "Line Subtotal",
        ]],
        body: tripRows,
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "top" },
        headStyles: { fillColor: [44, 76, 59], textColor: 255, fontSize: 9, cellPadding: 5 },
        alternateRowStyles: { fillColor: [250, 250, 247] },
        columnStyles: {
          0: { cellWidth: 64 },
          1: { cellWidth: 200, overflow: "linebreak" },
          2: { cellWidth: 70, halign: "center" },
          3: { cellWidth: 110 },
          4: { cellWidth: 200, overflow: "linebreak" },
          5: { cellWidth: 88, halign: "right", fontStyle: "bold" },
        },
        margin: { left: 28, right: 28 },
        foot: [[
          "TOTALS",
          `${enriched.length} trips`,
          `${totalKmInPeriod.toFixed(0)} km\n${totalEligibleKm.toFixed(0)} elig.`,
          "",
          `Mileage ${fmtCAD(totalMileageClaim)}`,
          fmtCAD(totalEligibleTravel),
        ]],
        footStyles: { fillColor: [235, 233, 224], textColor: 30, fontStyle: "bold", fontSize: 9 },
        didDrawPage: (data) => {
          // Re-render header strip on each page so the audit trail looks branded
          // (We leave existing footer logic to the final loop)
        },
      });
      cursorY = doc.lastAutoTable.finalY + 22;
    }

    // ========== 2. PRESCRIPTION & SERVICES LEDGER ==========
    const rxEntries = yearEntries.filter((t) => n(t.drug_amount) > 0 || t.entry_type === "prescription");
    if (rxEntries.length) {
      if (cursorY > H - 140) { doc.addPage(); cursorY = 40; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(193, 119, 103);
      doc.text(`2. Prescription & Services Ledger  ·  ${rxEntries.length} entr${rxEntries.length === 1 ? "y" : "ies"}  ·  Always eligible`, 40, cursorY);
      cursorY += 6;

      const isServiceLike = (t) => {
        // Services / devices typically have no DIN — show N/A
        return t.service_kind === "service" || !t.din;
      };
      const rxRows = rxEntries.map((t) => [
        fmtDate(t.date),
        (t.drug_pharmacy || t.provider || "—"),
        t.drug_name || (t.service_kind === "service" ? "Medical service" : "Prescription"),
        isServiceLike(t) ? "N/A" : t.din,
        fmtCAD(n(t.drug_amount)),
      ]);

      autoTable(doc, {
        startY: cursorY,
        head: [[
          "Date Purchased",
          "Pharmacy / Provider",
          "Medication / Service",
          "DIN",
          "Net Out-of-Pocket",
        ]],
        body: rxRows,
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "top" },
        headStyles: { fillColor: [193, 119, 103], textColor: 255, fontSize: 9, cellPadding: 5 },
        alternateRowStyles: { fillColor: [255, 247, 238] },
        columnStyles: {
          0: { cellWidth: 82 },
          1: { cellWidth: 200, overflow: "linebreak" },
          2: { cellWidth: 250, overflow: "linebreak" },
          3: { cellWidth: 86, halign: "center" },
          4: { cellWidth: 110, halign: "right", fontStyle: "bold" },
        },
        margin: { left: 28, right: 28 },
        foot: [["TOTALS", "", `${rxEntries.length} entries`, "", fmtCAD(totalRx)]],
        footStyles: { fillColor: [255, 240, 226], textColor: 30, fontStyle: "bold", fontSize: 9 },
      });
      cursorY = doc.lastAutoTable.finalY + 22;
    }

    // Per-trip notes & companion certifications (audit-trail context)
    // SECTION 3 contract: ONLY explicit user-entered data — never system flags,
    // never AI output, never ineligibility messages (those live in Section 1).
    const isMeaningfulNote = (s) => {
      const t = (s || "").trim();
      return t.length > 0 && t.length <= 500;
    };
    const tripsWithExtraInfo = enriched.filter(({ t }) =>
      isMeaningfulNote(t.notes) || t.companion_required
    );
    if (tripsWithExtraInfo.length) {
      if (cursorY > H - 100) { doc.addPage(); cursorY = 40; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(44, 76, 59);
      doc.text("3. User notes & companion certifications", 40, cursorY);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(
        "Notes entered by the taxpayer; system-generated eligibility flags appear in Section 1.",
        40, cursorY + 12
      );
      cursorY += 22;
      autoTable(doc, {
        startY: cursorY,
        head: [["Date", "Destination", "User-entered notes / certification"]],
        body: tripsWithExtraInfo.map(({ t }) => {
          const parts = [];
          // Companion certification — comes from explicit user toggle + cert date/doctor
          if (t.companion_required) {
            const certBits = [];
            if (t.companion_cert_doctor) certBits.push(t.companion_cert_doctor);
            if (t.companion_cert_date) certBits.push(fmtDate(t.companion_cert_date));
            const cert = certBits.length ? ` (${certBits.join(", ")})` : "";
            parts.push(`Companion travel certified${cert}`);
          }
          // Strictly the t.notes free-text field (no system fields)
          if (isMeaningfulNote(t.notes)) parts.push(t.notes.trim());
          return [
            fmtDate(t.date),
            t.destination || t.purpose || "Trip",
            parts.join(" — "),
          ];
        }),
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "top" },
        headStyles: { fillColor: [92, 102, 96], textColor: 255, fontSize: 9 },
        columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 180, overflow: "linebreak" }, 2: { cellWidth: "auto", overflow: "linebreak" } },
        margin: { left: 28, right: 28 },
      });
      cursorY = doc.lastAutoTable.finalY + 16;
    }

    // ========== TOTALS BY CRA CATEGORY ==========
    const catTotals = {};
    yearEntries.forEach((t) => {
      const cat = t.category || (t.entry_type === "prescription" ? "Prescription" : "Travel");
      if (!catTotals[cat]) catTotals[cat] = { count: 0, amount: 0 };
      catTotals[cat].count += 1;
      // Approximation: full claimable contribution per entry
      const isTrip = t.entry_type !== "prescription";
      const trav = isTrip ? eligibleTripBreakdown(t, mileageRate).eligible : 0;
      catTotals[cat].amount += trav + n(t.drug_amount);
    });
    const catRows = Object.entries(catTotals)
      .filter(([, v]) => v.amount > 0 || v.count > 0)
      .sort((a, b) => b[1].amount - a[1].amount)
      .map(([cat, v]) => [cat, v.count, fmtCAD(v.amount)]);
    if (catRows.length) {
      if (cursorY > H - 120) { doc.addPage(); cursorY = 40; }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(44, 76, 59);
      doc.text("Totals by CRA category", 40, cursorY);
      cursorY += 6;
      autoTable(doc, {
        startY: cursorY,
        head: [["Category", "Entries", "Eligible amount"]],
        body: catRows,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [44, 76, 59], textColor: 255, fontSize: 9 },
        alternateRowStyles: { fillColor: [250, 250, 247] },
        columnStyles: {
          0: { cellWidth: 220 },
          1: { cellWidth: 80, halign: "right" },
          2: { cellWidth: 120, halign: "right", fontStyle: "bold" },
        },
        margin: { left: 40, right: 40 },
      });
      cursorY = doc.lastAutoTable.finalY + 16;
    }

    // ========== APPENDIX A — RECEIPT & LETTER THUMBNAILS ==========
    // Collect every (trip, fileId, kind) tuple within the claim period so an
    // auditor can visually substantiate each line item without the ZIP archive.
    // Letters are tagged with kind="letter" so the cell label reads
    // "Appointment Letter" instead of "Receipt".
    const receiptPairs = [];
    enriched.forEach(({ t }) => {
      (t.receipt_file_ids || []).forEach((fid) => receiptPairs.push({ t, fid, kind: "receipt" }));
      (t.appointment_letter_file_ids || []).forEach((fid) => receiptPairs.push({ t, fid, kind: "letter" }));
    });
    yearRx.forEach((t) => {
      (t.receipt_file_ids || []).forEach((fid) => receiptPairs.push({ t, fid, kind: "receipt" }));
      (t.appointment_letter_file_ids || []).forEach((fid) => receiptPairs.push({ t, fid, kind: "letter" }));
    });

    if (receiptPairs.length > 0) {
      const toastId = toast.loading(`Embedding ${receiptPairs.length} document${receiptPairs.length === 1 ? "" : "s"} into PDF…`);
      // Fetch all in parallel (8-wide pool to avoid hammering the API)
      const fetched = [];
      const CONCURRENCY = 8;
      for (let i = 0; i < receiptPairs.length; i += CONCURRENCY) {
        const batch = receiptPairs.slice(i, i + CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.all(batch.map(({ t, fid, kind }) =>
          fetchReceiptThumb(api, fid).then((r) => ({ ...r, t, kind }))
        ));
        fetched.push(...results);
      }
      toast.dismiss(toastId);

      // Appendix cover
      doc.addPage();
      cursorY = 40;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.setTextColor(30, 36, 32);
      doc.text("Appendix A — Receipts & Appointment Letters", 40, cursorY);
      cursorY += 16;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(92, 102, 96);
      doc.text(
        "Visual substantiation of every expense in this report. Thumbnails are embedded for image files (JPEG/PNG); PDF & HEIC files are listed by date and can be retrieved from the ZIP backup (Settings → Download all receipts). Appointment letters are included as CRA audit proof for travel claims over 40 km.",
        40, cursorY, { maxWidth: W - 80 }
      );
      cursorY += 36;

      // 3-column grid; each cell = label + image area
      const COLS = 3;
      const GAP = 14;
      const cellW = (W - 80 - GAP * (COLS - 1)) / COLS;     // width of one thumbnail cell
      const imgMaxH = 180;                                  // max thumbnail height in pt
      const labelH = 28;                                    // label band above image
      let col = 0;
      let rowTopY = cursorY;
      let rowMaxH = 0;

      const skipped = fetched.filter((r) => r.skipped);
      const embeddable = fetched.filter((r) => !r.skipped);

      embeddable.forEach((r, idx) => {
        // Compute thumb dimensions (preserve aspect ratio inside cellW × imgMaxH)
        const ratio = r.h > 0 ? r.w / r.h : 1;
        let drawW = cellW;
        let drawH = drawW / ratio;
        if (drawH > imgMaxH) {
          drawH = imgMaxH;
          drawW = drawH * ratio;
        }
        const cellH = labelH + drawH + 6;

        // Page break if next cell row exceeds usable height
        if (rowTopY + cellH > H - 60) {
          doc.addPage();
          rowTopY = 40;
          rowMaxH = 0;
          col = 0;
        }

        const cellX = 40 + col * (cellW + GAP);
        // Label band — date · short destination · CAD amount
        const t = r.t;
        const dateStr = fmtDate(t.date);
        const dest = (t.destination || t.drug_name || t.purpose || "Trip").slice(0, 42);
        const amt = t.entry_type === "prescription"
          ? fmtCAD(n(t.drug_amount))
          : fmtCAD(tripTotal(t));

        doc.setFillColor(245, 243, 235);
        doc.roundedRect(cellX, rowTopY, cellW, labelH, 4, 4, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        // Letters get a distinct blue label so auditors can tell at a glance
        // which pages are doctor's letters vs paid expense receipts.
        const isLetter = r.kind === "letter";
        doc.setTextColor(isLetter ? 61 : 44, isLetter ? 111 : 76, isLetter ? 168 : 59);
        const tag = isLetter ? "Letter" : "Receipt";
        doc.text(`${tag} ${idx + 1} of ${embeddable.length}  ·  ${dateStr}`, cellX + 8, rowTopY + 11);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(60);
        doc.text(dest, cellX + 8, rowTopY + 22, { maxWidth: cellW - 70 });
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 36, 32);
        doc.text(amt, cellX + cellW - 8, rowTopY + 22, { align: "right" });

        // Image — centred horizontally inside cell
        const imgX = cellX + (cellW - drawW) / 2;
        const imgY = rowTopY + labelH + 4;
        try {
          doc.addImage(r.img, r.fmt, imgX, imgY, drawW, drawH, undefined, "FAST");
        } catch {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text("(image failed to render)", cellX + 8, imgY + 12);
        }
        // Border around image for definition
        doc.setDrawColor(227, 225, 217);
        doc.setLineWidth(0.4);
        doc.rect(imgX, imgY, drawW, drawH);

        rowMaxH = Math.max(rowMaxH, cellH);
        col += 1;
        if (col >= COLS) {
          col = 0;
          rowTopY += rowMaxH + GAP;
          rowMaxH = 0;
        }
      });

      // Move cursor below last row
      if (col !== 0) {
        rowTopY += rowMaxH + GAP;
      }
      cursorY = rowTopY + 8;

      // List skipped receipts (PDFs / HEIC) so auditor knows they exist
      if (skipped.length > 0) {
        if (cursorY > H - 80) { doc.addPage(); cursorY = 40; }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(193, 119, 103);
        doc.text(`${skipped.length} additional receipt${skipped.length === 1 ? "" : "s"} not embedded (PDF / HEIC)`, 40, cursorY);
        cursorY += 14;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(80);
        doc.text(
          "These receipts are preserved in your ZIP backup (Settings → Download all receipts) and listed by date below for cross-reference.",
          40, cursorY, { maxWidth: W - 80 }
        );
        cursorY += 18;

        autoTable(doc, {
          startY: cursorY,
          head: [["Date", "Linked entry", "File type", "Receipt ID"]],
          body: skipped.map((r) => [
            fmtDate(r.t.date),
            (r.t.destination || r.t.drug_name || r.t.purpose || "Trip"),
            (r.contentType || "unknown"),
            r.fileId.slice(0, 8),
          ]),
          styles: { fontSize: 8, cellPadding: 3 },
          headStyles: { fillColor: [193, 119, 103], textColor: 255, fontSize: 8 },
          margin: { left: 40, right: 40 },
        });
      }
    }

    // Disclaimer
    if (cursorY > H - 60) { doc.addPage(); cursorY = 40; }
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(
      "Disclaimer: This report applies CRA's general distance-tier rules for medical travel and assumes substantially equivalent medical services were not available closer to home. Eligibility for specific expenses can depend on individual circumstances — please review with your accountant.",
      40, cursorY, { maxWidth: W - 80 }
    );

    // Footer on every page — small logo for brand presence in front of every accountant
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      if (logoDataUrl) {
        try { doc.addImage(logoDataUrl, "JPEG", 36, H - 32, 16, 16, undefined, "FAST"); } catch (err) { console.warn("Footer logo render failed", err); }
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(60);
      doc.text("MedEasy", 56, H - 22);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(140);
      doc.text(`mymedeasy.ca  ·  CRA Medical Expense Tracker  ·  ${range.label}`, 56, H - 12);
      doc.text(`Page ${i} of ${totalPages}`, W - 40, H - 18, { align: "right" });
    }
    doc.save(`medical_expenses_${range.slug}_CRA_detail.pdf`);
    toast.success("CRA-ready PDF downloaded");
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#5C6660] font-semibold">Logbook</div>
          <h1 className="font-[Manrope] text-4xl sm:text-5xl font-bold tracking-tight mt-2">Your medical trips</h1>
          <p className="text-[#5C6660] mt-2 max-w-lg">Each trip captures everything for one outing — kilometres, parking, meals, accommodation, and any drug costs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(currentYear)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="year-select" className="w-[120px] h-11 rounded-full border-[#E3E1D9]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="claim-period-trigger"
                className="rounded-full border-[#E3E1D9] h-11 px-4"
                title="CRA lets you claim any 12-month period ending in the tax year"
              >
                <CalendarBlank size={16} weight="duotone" className="mr-2 text-[#2C4C3B]" />
                {claimMode === "custom" && claimEndDate ? "12-mo. period" : "Calendar yr"}
                <CaretDown size={12} className="ml-1.5 text-[#5C6660]" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[340px] p-4 rounded-2xl border-[#E3E1D9]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6660] font-semibold mb-2">
                Claim period for export
              </div>
              <p className="text-xs text-[#5C6660] mb-3 leading-relaxed">
                CRA lets you claim any <strong className="text-[#1E2420]">12-month period</strong> ending in the tax year — not just January through December. Pick whichever window has the most expenses.
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-[#F7F6F2]" data-testid="claim-mode-calendar">
                  <input
                    type="radio"
                    name="claim-mode"
                    checked={claimMode === "calendar"}
                    onChange={() => setClaimMode("calendar")}
                    className="mt-1 accent-[#2C4C3B]"
                  />
                  <div>
                    <div className="text-sm font-medium">Calendar year {currentYear}</div>
                    <div className="text-[11px] text-[#5C6660]">Jan 1 – Dec 31, {currentYear}</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-[#F7F6F2]" data-testid="claim-mode-custom">
                  <input
                    type="radio"
                    name="claim-mode"
                    checked={claimMode === "custom"}
                    onChange={() => {
                      setClaimMode("custom");
                      if (!claimEndDate) setClaimEndDate(`${currentYear}-12-31`);
                    }}
                    className="mt-1 accent-[#2C4C3B]"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Custom 12-month period</div>
                    <div className="text-[11px] text-[#5C6660] mb-2">12 months ending on the date you choose</div>
                    {claimMode === "custom" && (
                      <div>
                        <Label htmlFor="claim-end" className="text-[10px] uppercase tracking-widest text-[#5C6660]">Period ends</Label>
                        <Input
                          id="claim-end"
                          data-testid="claim-end-date"
                          type="date"
                          value={claimEndDate}
                          max={`${currentYear}-12-31`}
                          min={`${currentYear - 1}-01-02`}
                          onChange={(e) => setClaimEndDate(e.target.value)}
                          className="mt-1 h-9 rounded-lg border-[#E3E1D9] text-sm"
                        />
                        {claimEndDate && (
                          <div className="text-[11px] text-[#2C4C3B] mt-2 font-medium" data-testid="claim-period-preview">
                            {fmtDate(claimRange().start)} → {fmtDate(claimRange().end)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </PopoverContent>
          </Popover>

          <Button data-testid="home-export-pdf" onClick={exportPDF} className="rounded-full bg-[#C17767] hover:bg-[#A45D4F] text-white h-11">
            <FilePdf size={18} weight="duotone" className="mr-2" /> PDF report
          </Button>
          <Button data-testid="home-export-csv" onClick={exportCSV} variant="outline" className="rounded-full border-[#E3E1D9] h-11">
            <FileCsv size={18} weight="duotone" className="mr-2" /> CSV
          </Button>
        </div>
      </div>

      {/* Quick-action circles — replaces the earlier row of three "Add"
          buttons. Rationale: each circle is instantly discoverable (zero
          taps to see all three entry types), the color of each circle
          matches its downstream dashboard tile so the cause-and-effect is
          visually obvious, and the layout scales gracefully on mobile.
          The 3-circle pattern is used by Apple Health, Airbnb, and most
          major banking apps — proven quick-action UX. */}
      <QuickActionsRow trips={trips} todayISO={todayISO()} />

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat
          Icon={Car}
          label="Kilometres driven"
          value={fmtKm(totals.km)}
          sublabel={`${totals.trip_count} trip${totals.trip_count === 1 ? "" : "s"} · ${fmtCAD(totals.mileage_credit || 0)} mileage credit`}
          testId="stat-km"
          accent="bg-[#2C4C3B]"
        />
        <Stat Icon={Receipt} label="Travel expenses" value={fmtCAD(totals.expenses)} sublabel="Parking · gas · meals · lodging" testId="stat-expenses" accent="bg-[#C17767]" />
        <Stat Icon={Pill} label="Prescriptions & services" value={fmtCAD(totals.drugs - premiumsTotal)} sublabel="Out of pocket" testId="stat-drugs" accent="bg-[#D4A373]" />
        <Stat Icon={ShieldCheck} label="Insurance premiums" value={fmtCAD(premiumsTotal)} sublabel="Blue Cross · Sun Life · workplace" testId="stat-premiums" accent="bg-[#2C4C3B]" />
        <Stat Icon={Stethoscope} label="Grand total" value={fmtCAD(totals.grand_total)} sublabel="Travel + Rx + premiums" testId="stat-total" accent="bg-[#1E2420]" />
      </div>

      {/* CRA 12-month period discovery tip (dismissible) */}
      <CraPeriodTip />

      {/* Estimated tax credit */}
      <TaxEstimateCard grandTotal={totals.grand_total} year={currentYear} />

      {/* Refund Guarantee — promises a refund if MedEasy didn't save you
          more than your subscription. Pulls a live snapshot from the
          backend so the user can see (savings - subscription_paid) at
          a glance and request a refund inline. */}
      <RefundGuaranteeCard />

      {/* Possible duplicate detector */}
      <DuplicateAlert trips={trips} />

      {/* Family Mode (MVP): filter chips for whose trips to show. Only
          renders when the user has at least one named family member
          besides Self (or any orphaned tag from existing trips). */}
      {memberChipNames.length > 1 && (
        <div
          className="-mx-1 px-1 overflow-x-auto"
          data-testid="family-filter-chips"
        >
          <div className="flex items-center gap-2 py-1 w-max">
            <button
              type="button"
              onClick={() => setMemberFilter("all")}
              data-testid="family-filter-all"
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                memberFilter === "all"
                  ? "bg-[#2C4C3B] text-white"
                  : "bg-[#F7F6F2] text-[#5C6660] hover:bg-[#EBE9E0] border border-[#E3E1D9]"
              }`}
            >
              All
            </button>
            {memberChipNames.map((name) => {
              const active = memberFilter.toLowerCase() === name.toLowerCase();
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setMemberFilter(name)}
                  data-testid={`family-filter-${name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? "bg-[#2C4C3B] text-white"
                      : "bg-[#F7F6F2] text-[#5C6660] hover:bg-[#EBE9E0] border border-[#E3E1D9]"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660]" />
        <Input
          data-testid="trip-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search trips — provider, destination, purpose…"
          className="pl-10 h-11 rounded-full border-[#E3E1D9]"
        />
      </div>

      {/* Trip list */}
      {tripsQ.isLoading ? (
        <div className="text-center text-[#5C6660] py-8">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl border-[#E3E1D9]">
          <CardContent className="p-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#EBE9E0] flex items-center justify-center">
              <Car weight="duotone" size={28} className="text-[#2C4C3B]/70" />
            </div>
            <h3 className="font-medium text-lg mt-4">{trips.length === 0 ? "No trips logged yet" : "No trips match your search"}</h3>
            <p className="text-[#5C6660] mt-1 mb-6">
              {trips.length === 0 ? "Log your first medical trip — everything for one outing in one form." : "Try a different search term or clear the filter."}
            </p>
            {trips.length === 0 && <TripFormDialog />}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="trip-list">
          {filtered.map((t) => (
            <TripCard key={t.id} trip={t} onDelete={() => del.mutate(t.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TripCard({ trip, onDelete }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const mileageRate = user?.custom_rate || CRA_MILEAGE_RATES[user?.province] || CRA_MILEAGE_RATES.ON;
  const isRx = (trip.entry_type === "prescription");
  // Premium entries are stored as `entry_type=prescription` with
  // category=Premium and service_kind=premium — route them to the
  // dedicated PremiumFormDialog instead of the generic Rx editor so the
  // user sees the insurer/plan/frequency UI they originally used.
  const isPremium = isRx && (
    (trip.category || "").toLowerCase() === "premium" ||
    trip.service_kind === "premium"
  );
  // Family Mode (MVP): pull the [Member] tag out of notes so we can
  // render a clean chip + strip it from the visible Notes block.
  const { member: tripMember, cleanNotes: tripCleanNotes } = parseNotesMember(trip.notes || "");
  const hasNonSelfMember = tripMember && tripMember.toLowerCase() !== "self";
  const expenses = isRx ? 0 : tripExpenses(trip);
  // Mileage dollar value at the user's provincial rate — only counted if the
  // trip clears the 40-km CRA tier (otherwise CRA gives $0 for travel).
  const oneWay = isRx ? 0 : n(trip.one_way_km);
  const mileageDollars = isRx
    ? 0
    : (oneWay >= 40 ? n(trip.total_km) * mileageRate : 0);
  // Card headline = expenses + drug + eligible mileage value
  const total = isRx ? n(trip.drug_amount) : (tripTotal(trip) + mileageDollars);
  const HeaderIcon = isRx ? Pill : Car;
  const headerAccent = isRx ? "bg-[#D4A373]" : "bg-[#2C4C3B]";

  const items = [
    { label: "Parking", v: n(trip.parking) },
    { label: "Tolls", v: n(trip.tolls) },
    {
      label: trip.meal_method === "flat_rate"
        ? `Meals (CRA flat rate · ${Number(trip.meal_flat_rate_count) || 0} × $23)`
        : "Meals",
      v: n(trip.meals),
    },
    { label: "Accommodation", v: n(trip.accommodation) },
    { label: "Gas", v: n(trip.gas) },
    { label: "Taxi", v: n(trip.taxi) },
    { label: "Rental", v: n(trip.rental) },
    { label: trip.other_desc || "Other", v: n(trip.other_amount) },
  ].filter((x) => x.v > 0);

  // Card row content differs slightly for prescriptions & premiums
  const titleText = isPremium
    ? (trip.drug_name || "Insurance Premium")
    : isRx
    ? (trip.drug_name || "Prescription")
    : (trip.destination || trip.purpose || "Medical trip");
  const subtitleText = isPremium
    ? (trip.drug_pharmacy || "Insurer")
    : isRx
    ? (trip.drug_pharmacy || "Pharmacy")
    : ([trip.provider, trip.purpose].filter(Boolean).join(" · ") || "—");

  return (
    <Card className="rounded-2xl border-[#E3E1D9] overflow-hidden" data-testid={`trip-card-${trip.id}`}>
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`trip-toggle-${trip.id}`}
          className="w-full text-left p-5 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 hover:bg-[#EBE9E0]/30 transition-colors"
        >
          <div className="min-w-0 flex items-start gap-4">
            <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${headerAccent}`}>
              <HeaderIcon weight="duotone" size={22} className="text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-[Manrope] font-semibold text-lg truncate">{titleText}</span>
                {hasNonSelfMember && (
                  <Badge
                    variant="outline"
                    data-testid={`trip-member-chip-${trip.id}`}
                    className="border-[#2C4C3B]/30 bg-[#EFF4EE] text-[#2C4C3B] text-[10px] uppercase tracking-wider gap-1"
                  >
                    <UsersThree size={11} weight="duotone" /> {tripMember}
                  </Badge>
                )}
                {trip.category && (
                  <Badge variant="outline" className="border-[#5C6660]/20 bg-[#F7F6F2] text-[#5C6660] text-[10px] uppercase tracking-wider">
                    {trip.category}
                  </Badge>
                )}
                {trip.companion_required && (
                  <Badge variant="outline" className="border-[#2C4C3B]/30 bg-[#EFF4EE] text-[#2C4C3B] text-[10px] uppercase tracking-wider">
                    + Companion
                  </Badge>
                )}
                {isPremium ? (
                  <Badge variant="outline" className="border-0 bg-[#2C4C3B]/15 text-[#2C4C3B] gap-1">
                    <ShieldCheck size={12} weight="bold" /> Insurance Premium
                  </Badge>
                ) : isRx ? (
                  <Badge variant="outline" className="border-0 bg-[#D4A373]/25 text-[#1E2420] gap-1">
                    <Pill size={12} weight="bold" /> Prescription
                  </Badge>
                ) : trip.round_trip === false ? (
                  <Badge variant="outline" className="border-[#E3E1D9] text-[#5C6660] gap-1">
                    <ArrowRight size={12} weight="bold" /> One-way
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-0 bg-[#2C4C3B]/10 text-[#2C4C3B] gap-1">
                    <ArrowsLeftRight size={12} weight="bold" /> Round trip
                  </Badge>
                )}
              </div>
              <div className="text-sm text-[#5C6660] mt-0.5 truncate">{subtitleText}</div>
              <div className="text-xs text-[#5C6660] mt-1 flex items-center gap-3 flex-wrap">
                <span>{fmtDate(trip.date)}</span>
                {!isRx && <span className="inline-flex items-center gap-1"><Car size={12} /> {fmtKm(trip.total_km || 0)}</span>}
                {!isRx && mileageDollars > 0 && (
                  <span className="inline-flex items-center gap-1 text-[#2C4C3B] font-semibold" data-testid={`trip-mileage-dollars-${trip.id}`}>
                    <Car size={12} weight="bold" /> {fmtCAD(mileageDollars)}
                  </span>
                )}
                {!isRx && expenses > 0 && <span className="inline-flex items-center gap-1"><Receipt size={12} /> {fmtCAD(expenses)}</span>}
                {n(trip.drug_amount) > 0 && <span className="inline-flex items-center gap-1"><Pill size={12} /> {fmtCAD(n(trip.drug_amount))}</span>}
              </div>
            </div>
          </div>
          <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 sm:gap-1">
            <div className="text-right">
              <div className="font-[Manrope] font-bold text-2xl text-[#1E2420]">{fmtCAD(total)}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#5C6660]">{isPremium ? "Annual premium" : isRx ? "Drug cost" : "Trip total"}</div>
            </div>
            <div className="sm:mt-1 text-[#5C6660]">
              {open ? <CaretUp size={18} /> : <CaretDown size={18} />}
            </div>
          </div>
        </button>

        {open && (
          <div className={`border-t border-[#E3E1D9] bg-[#FAFAF7] p-5 grid grid-cols-1 ${isRx ? "" : "md:grid-cols-3"} gap-5`} data-testid={`trip-detail-${trip.id}`}>
            {!isRx && (
              <>
                <DetailBlock title="Mileage" Icon={Car}>
                  <Row k="One-way" v={`${n(trip.one_way_km).toFixed(1)} km`} />
                  <Row k="Round trip" v={trip.round_trip === false ? "No" : "Yes"} />
                  <Row k="Total" v={fmtKm(trip.total_km || 0)} />
                  <Row k={`Rate (${user?.province || "ON"})`} v={`${(mileageRate * 100).toFixed(1)}¢/km`} />
                  <Row
                    k="Eligible mileage"
                    v={oneWay < 40 ? "$0.00 (under 40 km)" : fmtCAD(mileageDollars)}
                    bold
                  />
                </DetailBlock>

                <DetailBlock title="Expenses" Icon={Receipt}>
                  {items.length === 0 ? (
                    <div className="space-y-2.5">
                      <div className="text-sm text-[#5C6660]">No expenses logged.</div>
                      <TripFormDialog
                        initial={trip}
                        trigger={
                          <Button
                            size="sm"
                            className="rounded-full bg-[#2C4C3B] hover:bg-[#1E3329] text-white shadow-sm"
                            data-testid={`trip-add-receipt-${trip.id}`}
                          >
                            <Receipt size={14} className="mr-1.5" weight="duotone" /> Add receipt
                          </Button>
                        }
                      />
                    </div>
                  ) : (
                    <>
                      {items.map((it) => <Row key={it.label} k={it.label} v={fmtCAD(it.v)} />)}
                      <Row k="Subtotal" v={fmtCAD(expenses)} bold />
                      <div className="pt-2">
                        <TripFormDialog
                          initial={trip}
                          trigger={
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full border-[#2C4C3B]/40 text-[#2C4C3B] hover:bg-[#2C4C3B]/5 h-8 px-3"
                              data-testid={`trip-add-expense-${trip.id}`}
                            >
                              <Plus size={14} className="mr-1" weight="bold" /> Add expense
                            </Button>
                          }
                        />
                      </div>
                    </>
                  )}
                </DetailBlock>
              </>
            )}

            <DetailBlock title={isRx ? (trip.service_kind === "service" ? "Service" : "Prescription") : "Prescriptions & services"} Icon={Pill}>
              {n(trip.drug_amount) > 0 || n(trip.service_total) > 0 || isRx ? (
                <>
                  <Row k={trip.service_kind === "service" ? "Service" : "Medication"} v={trip.drug_name || "—"} />
                  <Row k={trip.service_kind === "service" ? "Provider" : "Pharmacy"} v={trip.drug_pharmacy || "—"} />
                  {trip.din && <Row k="DIN" v={trip.din} />}
                  {n(trip.service_total) > 0 && <Row k="Total" v={fmtCAD(n(trip.service_total))} />}
                  {n(trip.service_covered) > 0 && <Row k="Covered" v={fmtCAD(n(trip.service_covered))} />}
                  <Row k="Out of pocket" v={fmtCAD(n(trip.drug_amount))} bold />
                </>
              ) : (
                <div className="text-sm text-[#5C6660]">None.</div>
              )}
            </DetailBlock>

            {tripCleanNotes && (
              <div className={`${isRx ? "" : "md:col-span-3"} text-sm text-[#1E2420] bg-white rounded-xl border border-[#E3E1D9] p-4`}>
                <div className="text-[10px] uppercase tracking-widest text-[#5C6660] mb-1">Notes</div>
                {tripCleanNotes}
              </div>
            )}

            {(trip.receipt_file_ids || []).length > 0 && (
              <div className={isRx ? "" : "md:col-span-3"}>
                <div className="text-[10px] uppercase tracking-widest text-[#5C6660] mb-2">Receipts</div>
                <div className="flex flex-wrap gap-2">
                  {trip.receipt_file_ids.map((fid) => (
                    <ReceiptViewer
                      key={fid}
                      fileId={fid}
                      trigger={
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-[#E3E1D9]"
                          data-testid={`trip-receipt-${trip.id}-${fid}`}
                        >
                          <Paperclip size={14} className="mr-1.5" /> View
                        </Button>
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {(trip.appointment_letter_file_ids || []).length > 0 && (
              <div className={isRx ? "" : "md:col-span-3"}>
                <div className="text-[10px] uppercase tracking-widest text-[#3D6FA8] mb-2">Appointment letters</div>
                <div className="flex flex-wrap gap-2">
                  {trip.appointment_letter_file_ids.map((fid) => (
                    <ReceiptViewer
                      key={fid}
                      fileId={fid}
                      trigger={
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-[#3D6FA8]/30 text-[#3D6FA8]"
                          data-testid={`trip-letter-${trip.id}-${fid}`}
                        >
                          <Paperclip size={14} className="mr-1.5" /> View letter
                        </Button>
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            <div className={`${isRx ? "" : "md:col-span-3"} flex items-center justify-end gap-2 pt-2 flex-wrap`}>
              {!isRx && (
                // Web Share API path — pops the iOS native share sheet so
                // "Add to Calendar" shows up alongside Mail/Messages. This
                // works in PWA standalone mode where a direct <a> link to
                // a .ics URL just renders a blank in-app WebView preview.
                <Button
                  variant="outline"
                  onClick={() => shareTripCalendar(trip.id)}
                  data-testid={`trip-add-to-calendar-${trip.id}`}
                  className="rounded-full border-[#2C4C3B]/30 text-[#2C4C3B] hover:bg-[#2C4C3B]/5"
                >
                  <CalendarBlank size={16} className="mr-1.5" weight="duotone" /> Add to calendar
                </Button>
              )}
              {!isRx && (
                // Email-the-invite — Apple Mail's .ics attachment handler
                // is the most reliable iOS path; one tap from the email
                // shows the native "Add to Calendar" preview.
                <Button
                  variant="outline"
                  onClick={async () => {
                    const ok = await emailTripInvite(trip.id);
                    if (ok) toast.success("Invite emailed — check your inbox");
                    else toast.error("Couldn't send the email. Verify mymedeasy.ca in resend.com to email all users.");
                  }}
                  data-testid={`trip-email-invite-${trip.id}`}
                  className="rounded-full border-[#2C4C3B]/30 text-[#2C4C3B] hover:bg-[#2C4C3B]/5"
                >
                  <EnvelopeSimple size={16} className="mr-1.5" weight="duotone" /> Email me
                </Button>
              )}
              {isPremium ? (
                <PremiumFormDialog
                  initial={{ ...trip, id: undefined, date: todayISO() }}
                  trigger={
                    <Button variant="outline" className="rounded-full border-[#E3E1D9]" data-testid={`trip-duplicate-${trip.id}`}>
                      <Copy size={16} className="mr-1.5" /> Duplicate
                    </Button>
                  }
                />
              ) : isRx ? (
                <PrescriptionFormDialog
                  initial={{ ...trip, id: undefined, date: todayISO(), receipt_file_ids: [], appointment_letter_file_ids: [] }}
                  trigger={
                    <Button variant="outline" className="rounded-full border-[#E3E1D9]" data-testid={`trip-duplicate-${trip.id}`}>
                      <Copy size={16} className="mr-1.5" /> Duplicate
                    </Button>
                  }
                />
              ) : (
                <TripFormDialog
                  initial={{ ...trip, id: undefined, date: todayISO(), receipt_file_ids: [], appointment_letter_file_ids: [] }}
                  trigger={
                    <Button variant="outline" className="rounded-full border-[#E3E1D9]" data-testid={`trip-duplicate-${trip.id}`}>
                      <Copy size={16} className="mr-1.5" /> Duplicate
                    </Button>
                  }
                />
              )}
              {isPremium ? (
                <PremiumFormDialog
                  initial={trip}
                  trigger={
                    <Button variant="outline" className="rounded-full border-[#E3E1D9]" data-testid={`trip-edit-${trip.id}`}>
                      <PencilSimple size={16} className="mr-1.5" /> Edit
                    </Button>
                  }
                />
              ) : isRx ? (
                <PrescriptionFormDialog
                  initial={trip}
                  trigger={
                    <Button variant="outline" className="rounded-full border-[#E3E1D9]" data-testid={`trip-edit-${trip.id}`}>
                      <PencilSimple size={16} className="mr-1.5" /> Edit
                    </Button>
                  }
                />
              ) : (
                <TripFormDialog
                  initial={trip}
                  trigger={
                    <Button variant="outline" className="rounded-full border-[#E3E1D9]" data-testid={`trip-edit-${trip.id}`}>
                      <PencilSimple size={16} className="mr-1.5" /> Edit
                    </Button>
                  }
                />
              )}
              <Button
                variant="outline"
                onClick={onDelete}
                className="rounded-full border-[#C17767]/40 text-[#C17767] hover:bg-[#C17767]/10 hover:text-[#C17767]"
                data-testid={`trip-delete-${trip.id}`}
              >
                <Trash size={16} className="mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailBlock({ title, Icon, children }) {
  return (
    <div className="bg-white rounded-xl border border-[#E3E1D9] p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon weight="duotone" size={16} className="text-[#2C4C3B]" />
        <div className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">{title}</div>
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function DuplicateAlert({ trips }) {
  const [open, setOpen] = useState(false);
  const dups = useMemo(() => {
    // Find pairs: same date + amount within $1 + similar drug_name or destination
    const pairs = [];
    const sameAmt = (a, b) => Math.abs(a - b) < 1.0;
    const normalise = (s) => (s || "").toLowerCase().trim();
    for (let i = 0; i < trips.length; i++) {
      for (let j = i + 1; j < trips.length; j++) {
        const a = trips[i], b = trips[j];
        if (!a.date || a.date !== b.date) continue;
        const amtA = (Number(a.drug_amount) || 0) + (Number(a.service_total) || 0);
        const amtB = (Number(b.drug_amount) || 0) + (Number(b.service_total) || 0);
        if (amtA <= 0 || amtB <= 0) continue;
        if (!sameAmt(amtA, amtB)) continue;
        const nameA = normalise(a.drug_name) || normalise(a.destination);
        const nameB = normalise(b.drug_name) || normalise(b.destination);
        if (!nameA || !nameB) continue;
        // Match if names share at least 4 chars overlap or one starts with the other
        const overlap = nameA.length >= 4 && nameB.length >= 4 && (
          nameA.includes(nameB.slice(0, 4)) ||
          nameB.includes(nameA.slice(0, 4)) ||
          nameA === nameB
        );
        if (overlap) pairs.push({ a, b });
      }
    }
    return pairs;
  }, [trips]);

  if (dups.length === 0) return null;

  return (
    <Card className="rounded-2xl border-[#D4A373]/40 bg-[#FFF6E8]" data-testid="duplicate-alert">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#D4A373] flex items-center justify-center shrink-0">
            <Copy weight="duotone" size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B5A2B] font-semibold">
              {dups.length} possible duplicate {dups.length === 1 ? "entry" : "entries"} detected
            </div>
            <div className="text-sm text-[#1E2420] mt-1 leading-relaxed">
              Same date and similar amount — you may have logged this twice (e.g. once as a trip and once as a standalone prescription). Duplicates inflate your claim.
            </div>
            <button
              data-testid="duplicate-alert-toggle"
              onClick={() => setOpen((o) => !o)}
              className="mt-2 text-xs font-semibold text-[#8B5A2B] hover:underline"
            >
              {open ? "Hide details ↑" : "Show details ↓"}
            </button>
            {open && (
              <div className="mt-3 space-y-3" data-testid="duplicate-list">
                {dups.slice(0, 10).map((p) => (
                  <div key={`${p.a.id || p.a.date}-${p.b.id || p.b.date}`} className="rounded-xl bg-white/70 border border-[#D4A373]/30 p-3 text-xs">
                    <div className="font-medium text-[#1E2420] mb-1">
                      {fmtDate(p.a.date)} · {p.a.drug_name || p.a.destination || "Untitled"}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-[#5C6660]">
                      <div>
                        <span className="text-[#8B5A2B] font-semibold">Entry A:</span> {fmtCAD((Number(p.a.drug_amount) || 0) + (Number(p.a.service_total) || 0))} · {p.a.entry_type === "prescription" ? "Rx" : "Trip"}
                      </div>
                      <div>
                        <span className="text-[#8B5A2B] font-semibold">Entry B:</span> {fmtCAD((Number(p.b.drug_amount) || 0) + (Number(p.b.service_total) || 0))} · {p.b.entry_type === "prescription" ? "Rx" : "Trip"}
                      </div>
                    </div>
                  </div>
                ))}
                {dups.length > 10 && (
                  <div className="text-[11px] text-[#5C6660] italic">+ {dups.length - 10} more pairs not shown</div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CraPeriodTip() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("medeasy_cra_period_tip_dismissed") === "1"; }
    catch { return false; }
  });
  if (dismissed) return null;
  const close = () => {
    try { localStorage.setItem("medeasy_cra_period_tip_dismissed", "1"); } catch (err) { console.warn("localStorage blocked", err); }
    setDismissed(true);
  };
  return (
    <Card
      className="rounded-2xl border-[#2C4C3B]/20 bg-[#F0F4EF] overflow-hidden"
      data-testid="cra-period-tip"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0">
            <CalendarBlank weight="duotone" size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#2C4C3B] font-semibold">
              CRA tip · most Canadians miss this
            </div>
            <div className="text-sm text-[#1E2420] mt-1.5 leading-relaxed">
              You can claim <strong>any 12-month period ending in the tax year</strong> — not just January through December. If your heavy treatment months span two calendar years, picking the right window can add 10–20% to your eligible amount.
            </div>
            <div className="text-xs text-[#5C6660] mt-2">
              Tap <strong className="text-[#2C4C3B]">Calendar yr ▾</strong> above your PDF button to pick a custom period.
            </div>
          </div>
          <button
            onClick={close}
            data-testid="cra-period-tip-dismiss"
            className="text-[#5C6660] hover:text-[#1E2420] text-xs shrink-0 -mt-1"
            aria-label="Dismiss tip"
          >
            ✕
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// Provincial rates + calculation moved to /lib/taxCredit.js so /log,
// /mileage, and the backend all show the SAME tax-credit number.
import { PROVINCIAL_RATES, calcMedicalCredit, FALLBACK_INCOME, FEDERAL_RATE } from "@/lib/taxCredit";

function TaxEstimateCard({ grandTotal, year }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const defaultProv = (user?.province && PROVINCIAL_RATES[user.province]) ? user.province : "ON";
  const [prov, setProv] = useState(defaultProv);

  useEffect(() => {
    if (user?.province && PROVINCIAL_RATES[user.province]) setProv(user.province);
  }, [user?.province]);

  const {
    eligible, credit, remainingToCredit, combinedRate, provinceInfo,
    needsIncomeSetup, threshold,
  } = calcMedicalCredit({
    totalMedicalExpenses: grandTotal,
    netIncome: user?.net_income,
    province: prov,
  });

  return (
    <Card className="rounded-2xl border-[#C17767]/30 bg-gradient-to-br from-[#FFF7EE] to-[#F7F6F2] overflow-hidden" data-testid="tax-estimate-card">
      <CardContent className="p-6 sm:p-7">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-[#C17767] flex items-center justify-center shrink-0">
            <Receipt weight="duotone" size={24} className="text-white" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660] font-semibold">
                Estimated medical tax credit · {year}
              </div>
              <Select value={prov} onValueChange={setProv}>
                <SelectTrigger
                  data-testid="tax-province-select"
                  className="h-8 w-[160px] rounded-full border-[#C17767]/30 bg-white text-xs"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROVINCIAL_RATES).map(([code, { name }]) => (
                    <SelectItem key={code} value={code}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {eligible > 0 ? (
              <>
                <div className="font-[Manrope] font-bold text-3xl sm:text-4xl text-[#1E2420] mt-3" data-testid="tax-estimate-value">
                  {fmtCAD(credit)}
                </div>
                <div className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                  You could claim ~<strong className="text-[#1E2420]">{fmtCAD(eligible)}</strong> in eligible medical expenses (after the CRA 3% threshold of {fmtCAD(threshold)}).
                  Combined federal ({(FEDERAL_RATE * 100).toFixed(2).replace(/\.?0+$/, "")}%) + {provinceInfo.name} ({(provinceInfo.rate * 100).toFixed(2)}%) credit ≈ <strong>{(combinedRate * 100).toFixed(1)}%</strong>.
                </div>
              </>
            ) : (
              <>
                <div className="font-[Manrope] font-bold text-2xl sm:text-3xl text-[#1E2420] mt-3" data-testid="tax-estimate-value">
                  {fmtCAD(remainingToCredit)} to go
                </div>
                <div className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                  Tax credit kicks in once your medical expenses exceed the CRA 3% threshold ({fmtCAD(threshold)}{needsIncomeSetup ? ` — assumes ${fmtCAD(FALLBACK_INCOME)} income` : ""}).
                  Keep logging — every receipt counts.
                </div>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#C17767]/30 text-xs" data-testid="tax-rate-preview">
                  <span className="text-[#5C6660]">After threshold,</span>
                  <strong className="text-[#1E2420]">{provinceInfo.name}</strong>
                  <span className="text-[#5C6660]">returns</span>
                  <strong className="text-[#2C4C3B]">{(combinedRate * 100).toFixed(1)}¢</strong>
                  <span className="text-[#5C6660]">per $1 spent</span>
                </div>
              </>
            )}
            {needsIncomeSetup ? (
              <button
                onClick={() => navigate("/settings")}
                data-testid="tax-estimate-set-income"
                className="text-[11px] text-[#2C4C3B] hover:underline mt-3 font-semibold text-left"
              >
                Estimate uses {fmtCAD(FALLBACK_INCOME)} as a placeholder. Tap here to set your real net income in Settings for an accurate CRA number →
              </button>
            ) : (
              <div className="text-[11px] text-[#5C6660]/80 mt-3">
                Based on your net income of {fmtCAD(user.net_income)} · CRA 3% threshold = {fmtCAD(threshold)}. Confirm with your accountant.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ k, v, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#5C6660]">{k}</span>
      <span className={bold ? "font-semibold text-[#1E2420]" : "text-[#1E2420]"}>{v}</span>
    </div>
  );
}



/**
 * RefundGuaranteeCard — live "did we save you money?" widget on the dashboard.
 *
 * Calls /api/savings/summary to get:
 *   • estimated_credit_cad — what we think you'll get back from CRA
 *   • subscription_paid_cad — lifetime $$ paid to MedEasy
 *   • refund_eligible — true iff credit < paid AND at least one trip logged
 *
 * Shows a calm "On track to save $X" message when the user is in the green,
 * and a "Request your refund" CTA when they're not. The refund-request
 * endpoint files a ticket for owner review (no auto-stripe refund yet).
 */
function RefundGuaranteeCard() {
  const { user, isAuthed } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestNote, setRequestNote] = useState("");
  const [requestBusy, setRequestBusy] = useState(false);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    setBusy(true);
    api.get("/savings/summary")
      .then((r) => { if (!cancelled) setSnapshot(r.data); })
      .catch(() => { /* silent — endpoint is best-effort */ })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [isAuthed, user?.id]);

  const submitRefund = async () => {
    setRequestBusy(true);
    try {
      await api.post("/billing/refund-request", { note: requestNote || null });
      toast.success("Refund request received", {
        description: "We'll review and reply within 2 business days.",
      });
      setRequestOpen(false);
      setRequestNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send refund request");
    } finally {
      setRequestBusy(false);
    }
  };

  if (!isAuthed) return null;
  if (busy && !snapshot) {
    return (
      <Card className="rounded-2xl border-[#E3E1D9]" data-testid="refund-guarantee-card">
        <CardContent className="p-6 text-sm text-[#5C6660]">Calculating your savings…</CardContent>
      </Card>
    );
  }
  if (!snapshot) return null;

  const credit = Number(snapshot.estimated_credit_cad || 0);
  const paid = Number(snapshot.subscription_paid_cad || 0);
  const netSavings = credit - paid;
  const eligible = !!snapshot.refund_eligible;
  const netIncomeUsed = Number(snapshot.net_income_used || 0);
  const netIncomeIsFallback = !!snapshot.net_income_is_fallback;
  // No subscription paid yet (comp code / trial) — show a neutral "savings
  // tracker" framing instead of the refund-guarantee variant.
  const isFreeUser = paid <= 0;

  return (
    <Card
      className={`rounded-2xl overflow-hidden ${
        eligible
          ? "border-[#C17767]/40 bg-gradient-to-br from-[#FFF4ED] to-[#F7F6F2]"
          : "border-[#2C4C3B]/30 bg-gradient-to-br from-[#EFF4EE] to-[#F7F6F2]"
      }`}
      data-testid="refund-guarantee-card"
    >
      <CardContent className="p-6 sm:p-7">
        <div className="flex items-start gap-5 flex-wrap">
          <div className={`w-12 h-12 rounded-2xl ${eligible ? "bg-[#C17767]" : "bg-[#2C4C3B]"} flex items-center justify-center shrink-0`}>
            <ShieldCheck weight="duotone" size={24} className="text-white" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6660] font-semibold">
              {isFreeUser ? "Live savings tracker" : "Refund guarantee"}
            </div>
            {isFreeUser ? (
              <>
                <div className="font-[Manrope] font-bold text-3xl sm:text-4xl text-[#1E2420] mt-3" data-testid="refund-savings-value">
                  {fmtCAD(credit)}
                </div>
                <div className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                  Estimated tax credit so far this year. Keep snapping receipts — every logged dollar increases what CRA pays you back.
                </div>
              </>
            ) : (
              <>
                <div className="font-[Manrope] font-bold text-3xl sm:text-4xl text-[#1E2420] mt-3" data-testid="refund-savings-value">
                  {fmtCAD(Math.max(0, netSavings))}
                  <span className="text-base font-normal text-[#5C6660] ml-2">net</span>
                </div>
                <div className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                  Estimated tax credit ({fmtCAD(credit)}) minus your MedEasy subscription ({fmtCAD(paid)}).
                </div>
                <div className="text-[13px] text-[#1E2420] mt-3 leading-relaxed">
                  {eligible ? (
                    <>
                      Your subscription has cost more than what we&apos;ve helped you claim so far. <strong>You qualify for our Refund Guarantee.</strong>
                    </>
                  ) : (
                    <>
                      You&apos;re on track. <strong>If MedEasy ever costs you more than it saves, we&apos;ll refund what you paid us — no questions, no fine print.</strong>
                    </>
                  )}
                </div>
                {eligible && (
                  <div className="mt-4">
                    <Button
                      onClick={() => setRequestOpen(true)}
                      data-testid="refund-request-btn"
                      className="rounded-full bg-[#C17767] hover:bg-[#AD6757] text-white"
                    >
                      Request your refund
                    </Button>
                  </div>
                )}
              </>
            )}
            <div className="text-[11px] text-[#5C6660]/80 mt-3" data-testid="refund-guarantee-disclaimer">
              {netIncomeIsFallback
                ? `Estimate uses ${fmtCAD(FALLBACK_INCOME)} as a placeholder net income — set your real net income in Settings for an accurate CRA number.`
                : `Based on your net income of ${fmtCAD(netIncomeUsed)} · combined federal + provincial rate · CRA 3% threshold applied.`}
            </div>
          </div>
        </div>
      </CardContent>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Request your refund</DialogTitle>
            <DialogDescription>
              We&apos;ll review your savings vs. subscription within 2 business days and refund via Stripe.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl border border-[#E3E1D9] bg-[#F7F6F2] p-3 text-sm">
              <Row k="Subscription paid" v={fmtCAD(paid)} />
              <Row k="Estimated tax credit" v={fmtCAD(credit)} />
              <Row k="Net" v={fmtCAD(netSavings)} bold />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Note (optional)</Label>
              <Textarea
                rows={3}
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                placeholder="Anything we should know?"
                data-testid="refund-request-note"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRequestOpen(false)}
              data-testid="refund-request-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRefund}
              disabled={requestBusy}
              data-testid="refund-request-submit"
              className="bg-[#C17767] hover:bg-[#AD6757] text-white"
            >
              {requestBusy ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}



/**
 * QuickActionsRow — three circular quick-add buttons on the dashboard.
 *
 * Replaces the earlier button row of [Rx / Service only] + [Add insurance
 * premium] + [Log a trip]. Rationale:
 *   • Zero taps to see all three entry types (fixes the discoverability
 *     problem that made a user log a $3,500 Sun Life premium as an Rx).
 *   • Each circle's color mirrors the downstream dashboard tile so
 *     cause-and-effect is visually obvious (tap green → kilometres grows).
 *   • Circle pattern scales beautifully on mobile — no button-wrap issues.
 *
 * Wiring:
 *   • Trip circle    → LogTripButton (which owns the same-day-trip
 *                       chooser dialog + smart new-trip logic).
 *   • Rx circle      → PrescriptionFormDialog (existing).
 *   • Premium circle → PremiumFormDialog (added this session).
 */
function QuickActionsRow({ trips, todayISO }) {
  return (
    <div
      data-testid="dashboard-quick-actions"
      className="flex items-start justify-center gap-6 sm:gap-10 py-2"
    >
      <LogTripButton
        trips={trips}
        todayISO={todayISO}
        trigger={
          <QuickActionCircle
            testId="qa-trip"
            Icon={Car}
            label="Medical Trip"
            bg="bg-[#2C4C3B]"
            hoverBg="hover:bg-[#3A634D]"
          />
        }
      />
      <PrescriptionFormDialog
        trigger={
          <QuickActionCircle
            testId="qa-rx"
            Icon={Pill}
            label="Rx / Service"
            bg="bg-[#C17767]"
            hoverBg="hover:bg-[#AD6757]"
          />
        }
      />
      <PremiumFormDialog
        trigger={
          <QuickActionCircle
            testId="qa-premium"
            Icon={ShieldCheck}
            label="Insurance"
            bg="bg-[#2C4C3B]"
            hoverBg="hover:bg-[#3A634D]"
          />
        }
      />
    </div>
  );
}

/**
 * QuickActionCircle — the visual for one quick-action button.
 *
 * A 64px filled circle with a duotone icon at the center, a small "+"
 * badge in the top-right (universal "add" affordance), and a two-line
 * label beneath. Motion: subtle scale-up on hover/active so the tap is
 * satisfying without being noisy.
 *
 * NOTE: Must be a forwardRef component that spreads `...props` onto the
 * underlying <button>, because Radix Dialog's <DialogTrigger asChild>
 * uses Radix Slot to inject its own onClick + ref onto the trigger
 * element. Without forwardRef + props-spread, the Rx and Insurance
 * dialogs would silently fail to open when the circle is tapped.
 */
const QuickActionCircle = React.forwardRef(function QuickActionCircle(
  { testId, Icon, label, bg, hoverBg, ...props },
  ref,
) {
  return (
    <button
      type="button"
      ref={ref}
      data-testid={testId}
      className="group flex flex-col items-center gap-2 focus:outline-none"
      {...props}
    >
      <span
        className={`relative w-16 h-16 sm:w-[68px] sm:h-[68px] rounded-full ${bg} ${hoverBg} flex items-center justify-center shadow-md shadow-black/10 transition-transform duration-150 group-hover:scale-105 group-active:scale-95`}
      >
        <Icon size={28} weight="duotone" className="text-white" />
        {/* Small "+" badge in the top-right corner to reinforce that
            these circles ADD an entry (not just filter/navigate). */}
        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white text-[#1E2420] flex items-center justify-center shadow-sm border border-[#E3E1D9]">
          <Plus size={12} weight="bold" />
        </span>
      </span>
      <span className="text-xs sm:text-[13px] font-[Manrope] font-semibold text-[#1E2420] leading-tight text-center">
        {label}
      </span>
    </button>
  );
});
